import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import omit from 'lodash/omit';

import { ISpecOffer, ISpecOfferNavigation } from '@r1-frontend/api-domru/spec-offer/v1/spec-offers/dto/ISpecOffer';

import { specialOfferAnalytics } from '~/src/features/specialOffersContainer/analytics';
import { DefaultCallback } from '~/src/features/specialOffersContainer/constants';
import { isCallbackDefined } from '~/src/features/specialOffersContainer/helpers';
import { setPopupState } from '~/src/features/specialOffersContainer/store/actions';
import { TPromoCallbackMap } from '~/src/features/specialOffersContainer/types';

type TButtonTextMap = {
    [key in ISpecOfferNavigation['type'] | 'default']: string;
}

interface IUseConnectOffer {
    isLocalLoading: boolean,
    activateButtonText: string,
    connectOffer: () => Promise<void>,
}

export const useConnectOffer = (callbackMap: TPromoCallbackMap<typeof DefaultCallback>, offer?: ISpecOffer): IUseConnectOffer => {
    const { billingState, navigation } = offer || {};

    const dispatch = useDispatch();

    const [isLocalLoading, setIsLocalLoading] = useState(false);

    const activateButtonText = useMemo(() => {
        const isOfferConnected = billingState === 'connected';

        const textMap: TButtonTextMap = {
            equipment: 'Заказать',
            promoPacket: 'Перейти к тарифу',
            pendingConnection: 'Проверить заявку',
            multisubscription: isOfferConnected ? 'Управлять подписками' : 'Выбрать подписки',
            speedBonusWithRequest: isOfferConnected ? 'Отключить' : 'Подключить',
            default: `${isOfferConnected ? 'Отключить' : 'Подключить'} сейчас`,
        };

        return textMap[navigation?.type ?? 'default'];
    }, [billingState, navigation?.type]);

    const connectOffer = useCallback(async() => {
        if (!offer) {
            return;
        }

        try {
            const [filtered] = Object
                .values(omit(callbackMap, [DefaultCallback]))
                .filter(isCallbackDefined)
                .map(factory => factory(offer))
                .filter(({ on }) => on());

            const targetCallback = filtered || callbackMap[DefaultCallback](offer);

            if (targetCallback) {
                specialOfferAnalytics.instantConnect(offer);

                const { loader, callback } = targetCallback;

                setIsLocalLoading(loader === 'local');
                await callback();
            }
        } catch {
            dispatch(setPopupState('connectionError'));
        }
    }, [callbackMap, offer, dispatch]);

    return { isLocalLoading, activateButtonText, connectOffer };
};
